'use client';

import { useInfiniteQuery } from '@tanstack/react-query';
import { useAccessToken } from '@/lib/auth-client';
import type { FileEntriesPage, FileEntryKind } from 'pc-types';

import { unwrapLambdaResponse } from '@/lib/api';
import { BACKEND_URL } from '@/lib/constants';

export type EntryFilter = 'all' | FileEntryKind;

// spaceId null = the cross-space aggregate ("All Documents"): every space the
// user can access, via the org-level /files/entries endpoint.
export function useFileEntries(
  spaceId: string | null,
  options: {
    // undefined = all folders, null = root, string = that folder.
    folderId?: string | null;
    filter: EntryFilter;
    search: string;
    orderBy?: 'updatedAt' | 'lastOpenedAt';
    enabled?: boolean;
  }
) {
  const { getAccessToken, loading: authLoading } = useAccessToken();
  const {
    folderId,
    filter,
    search,
    orderBy = 'updatedAt',
    enabled = true,
  } = options;

  return useInfiniteQuery({
    queryKey: [
      ...(spaceId ? ['space', spaceId, 'files'] : ['all-documents']),
      'entries',
      { folderId: folderId === undefined ? 'all' : folderId, filter, search, orderBy },
    ],
    queryFn: async ({ pageParam }) => {
      const token = await getAccessToken();
      if (!token) throw new Error('Not authenticated');
      const url = new URL(
        spaceId
          ? `${BACKEND_URL}/spaces/${spaceId}/files/entries`
          : `${BACKEND_URL}/files/entries`
      );
      if (spaceId && folderId !== undefined) {
        url.searchParams.set('folderId', folderId ?? 'null');
      }
      if (filter !== 'all') url.searchParams.set('kind', filter);
      if (search) url.searchParams.set('search', search);
      if (orderBy !== 'updatedAt') url.searchParams.set('orderBy', orderBy);
      if (pageParam) url.searchParams.set('cursor', pageParam);
      const res = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load files');
      return unwrapLambdaResponse<FileEntriesPage>(await res.json());
    },
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: !authLoading && enabled,
    refetchInterval: (query) => {
      // Keep polling while any file is still processing OR any deck import is
      // mid-conversion — a convert on an already-processed file changes only
      // deckImport.status, and without polling the row would sit at
      // "Converting…" until a manual refresh.
      const activeImportStatuses = ['pending', 'processing', 'queued', 'rebuilding'];
      const hasPending = query.state.data?.pages.some((page) =>
        page.entries.some(
          (entry) =>
            entry.kind === 'file' &&
            (entry.status === 'pending' ||
              entry.status === 'processing' ||
              (entry.deckImport &&
                activeImportStatuses.includes(entry.deckImport.status)))
        )
      );
      return hasPending ? 5000 : false;
    },
  });
}
